
import React from 'react';
import { SimulationConfig } from '@/types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { Camera } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';
import { InfoPopup } from './InfoPopup';

interface CameraSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const CameraSettingsSection: React.FC<CameraSettingsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "PRO", onUpgradeClick 
}) => {
  return (
    <CollapsibleSection 
        title="Camera View" 
        icon={Camera} 
        darkMode={config.darkMode} 
        settingKey="viewportOptics"
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
    >
        <div className="flex flex-col gap-6 mt-4 text-left">
            <div className="grid grid-cols-2 gap-3">
                <ModifierCard 
                    label="Follow Ball" 
                    settingKey="cameraFollow"
                    active={config.cameraFollow} 
                    onToggle={() => setConfig(p => ({...p, cameraFollow: !p.cameraFollow}))} 
                    darkMode={config.darkMode}
                />
                <ModifierCard 
                    label="Smart Pathing" 
                    settingKey="cameraPredictiveLookahead"
                    active={config.cameraPredictiveLookahead} 
                    onToggle={() => setConfig(p => ({...p, cameraPredictiveLookahead: !p.cameraPredictiveLookahead}))} 
                    darkMode={config.darkMode}
                />
            </div>

            <div className="flex flex-col gap-4">
                <SmartInput 
                    label="Smoothness" 
                    settingKey="cameraSmoothness"
                    min={0.01} max={1.0} step={0.01} 
                    value={config.cameraSmoothness} 
                    onChange={v => setConfig(p => ({...p, cameraSmoothness: v}))} 
                    darkMode={config.darkMode} 
                />
                <SmartInput 
                    label="Prediction" 
                    settingKey="cameraLookahead"
                    min={0} max={20} step={0.1} 
                    value={config.cameraLookahead} 
                    onChange={v => setConfig(p => ({...p, cameraLookahead: v}))} 
                    darkMode={config.darkMode} 
                />
                <SmartInput 
                    label="Elasticity" 
                    settingKey="cameraElasticity"
                    min={0} max={1.0} step={0.01} 
                    value={config.cameraElasticity} 
                    onChange={v => setConfig(p => ({...p, cameraElasticity: v}))} 
                    darkMode={config.darkMode} 
                />
                <SmartInput 
                    label="Motion Damping" 
                    settingKey="cameraDamping"
                    min={0.01} max={1.0} step={0.01} 
                    value={config.cameraDamping} 
                    onChange={v => setConfig(p => ({...p, cameraDamping: v}))} 
                    darkMode={config.darkMode} 
                />
                <SmartInput 
                    label="Deadzone" 
                    settingKey="cameraDeadzone"
                    min={0} max={200} step={1} 
                    value={config.cameraDeadzone} 
                    onChange={v => setConfig(p => ({...p, cameraDeadzone: v}))} 
                    darkMode={config.darkMode} 
                />
                <div className="h-px bg-zinc-200 dark:bg-white/10 my-2" />
                <SmartInput 
                    label="Zoom Level" 
                    settingKey="cameraZoom"
                    min={0.2} max={10.0} step={0.1}
                    value={config.cameraZoom} 
                    onChange={v => setConfig(p => ({...p, cameraZoom: v}))} 
                    darkMode={config.darkMode} 
                />
                <SmartInput 
                    label="Zoom Speed" 
                    settingKey="cameraZoomSmoothness"
                    min={0.01} max={1.0} step={0.01}
                    value={config.cameraZoomSmoothness} 
                    onChange={v => setConfig(p => ({...p, cameraZoomSmoothness: v}))} 
                    darkMode={config.darkMode} 
                />
            </div>
        </div>
    </CollapsibleSection>
  );
};

const ModifierCard = ({ label, active, onToggle, darkMode, settingKey }: any) => (
    <div className={`relative flex flex-col items-start gap-2 p-3 rounded-2xl border transition-all duration-300 group ${active ? (darkMode ? 'bg-white/10 border-white/10 text-white' : 'bg-white border-zinc-950 text-zinc-950 shadow-sm') : 'bg-transparent border-transparent text-zinc-400'}`}>
        <button className="absolute inset-0 w-full h-full cursor-pointer z-0" onClick={onToggle} />
        <div className="flex items-center gap-2 pointer-events-none z-10 w-full">
            <span className="text-[9px] font-black uppercase tracking-tight leading-none whitespace-nowrap flex-1">{label}</span>
            {settingKey && <div className="pointer-events-auto"><InfoPopup content="" settingKey={settingKey} darkMode={darkMode} /></div>}
        </div>
        <div className="pointer-events-none z-10 w-full">
            <Toggle active={active} onClick={onToggle} />
        </div>
    </div>
);
